import { formatDistanceToNow } from 'date-fns';
import { Bell, CheckCircle, AlertCircle, Info, TrendingUp, MessageSquare, UserPlus, Calendar, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Notification } from '@/hooks/useNotifications';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface NotificationPanelProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onMarkAllAsRead: () => void;
}

const getNotificationIcon = (type: string, priority: string) => {
  const iconClass = cn(
    'h-4 w-4 flex-shrink-0',
    priority === 'critical' && 'text-destructive',
    priority === 'high' && 'text-orange-500',
    priority === 'medium' && 'text-primary',
    priority === 'low' && 'text-muted-foreground'
  );

  switch (type) {
    case 'task_assigned':
    case 'task_due_soon':
    case 'task_overdue':
      return <CheckCircle className={iconClass} />;
    case 'opportunity_won':
    case 'opportunity_stage_changed':
      return <TrendingUp className={iconClass} />;
    case 'comment_added':
      return <MessageSquare className={iconClass} />;
    case 'new_email':
      return <Bell className={iconClass} />;
    case 'ai_suggestion':
      return <AlertCircle className={iconClass} />;
    case 'new_lead':
    case 'new_contact':
      return <UserPlus className={iconClass} />;
    case 'event_reminder':
      return <Calendar className={iconClass} />;
    default:
      return <Info className={iconClass} />;
  }
};

const groupNotifications = (notifications: Notification[]) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const thisWeek = new Date(today);
  thisWeek.setDate(thisWeek.getDate() - 7);

  const groups = {
    today: [] as Notification[],
    yesterday: [] as Notification[],
    thisWeek: [] as Notification[],
    older: [] as Notification[],
  };

  notifications.forEach(notification => {
    const date = new Date(notification.created_at);
    if (date >= today) {
      groups.today.push(notification);
    } else if (date >= yesterday) {
      groups.yesterday.push(notification);
    } else if (date >= thisWeek) {
      groups.thisWeek.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
};

export function NotificationPanel({
  notifications,
  onMarkAsRead,
  onDismiss,
  onMarkAllAsRead,
}: NotificationPanelProps) {
  const navigate = useNavigate();
  const groups = groupNotifications(notifications);
  const hasUnread = notifications.some(n => !n.is_read);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <Bell className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-sm text-muted-foreground">No notifications</p>
        <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold">Notifications</h3>
        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllAsRead}
            className="text-xs"
          >
            Mark all as read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <ScrollArea className="h-[400px]">
        <div className="divide-y">
          {Object.entries(groups).map(([groupName, groupNotifications]) => {
            if (groupNotifications.length === 0) return null;

            return (
              <div key={groupName}>
                <div className="px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50">
                  {groupName === 'today' && 'Today'}
                  {groupName === 'yesterday' && 'Yesterday'}
                  {groupName === 'thisWeek' && 'This Week'}
                  {groupName === 'older' && 'Older'}
                </div>
                {groupNotifications.map(notification => (
                  <div
                    key={notification.id}
                    className={cn(
                      'group relative flex gap-3 p-4 cursor-pointer transition-colors hover:bg-muted/50',
                      !notification.is_read && 'bg-primary/5'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Icon */}
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.notification_type, notification.priority)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 space-y-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          'text-sm font-medium leading-tight',
                          !notification.is_read && 'font-semibold'
                        )}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                        )}
                      </div>
                      {notification.message && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>

                    {/* Dismiss Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismiss(notification.id);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
